import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Plus, Search, Edit2, Trash2, Upload, UserCog, X, Download, ShieldCheck, Send, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { SPECIALIZATIONS, SERVICE_TYPES, AVAILABILITY_STATUSES } from '../../lib/constants';
import CsvImportDialog from '../../components/common/CsvImportDialog';
import LanguagePairsInput from '../../components/common/LanguagePairsInput';

const emptyInterpreter = {
  salutation: '', first_name: '', last_name: '', email: '', phone: '',
  street: '', house_number: '', postal_code: '', city: '', country: 'Deutschland',
  languages: [], target_languages: [], language_pairs: [],
  specializations: [], service_types: [],
  availability_status: 'verfuegbar', custom_rate: 0, active_status: true, sworn: false, notes: ''
};

const MultiSelect = ({ options, selected, onChange, placeholder, testid }) => {
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map(s => (
          <span key={s} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">
            {s}
            <button type="button" onClick={() => onChange(selected.filter(x => x !== s))} className="hover:text-red-600"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <select
        value=""
        onChange={(e) => { if (e.target.value && !selected.includes(e.target.value)) onChange([...selected, e.target.value]); }}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
      >
        <option value="">{placeholder}</option>
        {options.filter(o => !selected.includes(o.value ?? o)).map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
};

const AdminInterpreters = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInterpreter);
  const [loading, setLoading] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [swornOnly, setSwornOnly] = useState(false);

  const load = async () => { setItems((await api.get('/interpreters')).data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyInterpreter); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/interpreters/${editing.id}`, form);
        toast.success('Dolmetscher aktualisiert');
      } else {
        await api.post('/interpreters', form);
        toast.success('Dolmetscher angelegt');
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Dolmetscher wirklich löschen?')) return;
    await api.delete(`/interpreters/${id}`);
    toast.success('Gelöscht'); load();
  };

  const handleCsvComplete = (res) => {
    toast.success(`${res.imported} importiert, ${res.skipped} übersprungen`);
    load();
  };

  const downloadTemplate = () => {
    const header = 'salutation,first_name,last_name,email,phone,street,house_number,postal_code,city,country,source_language,target_language,specializations,service_types,sworn,custom_rate,notes';
    const example1 = 'Frau,Anna,Schmidt,anna.schmidt@example.com,+49 30 1234567,Musterstr.,1,10115,Berlin,Deutschland,Deutsch,Englisch,Recht;Medizin,video;telefon,ja,85.00,Vereidigt am LG Berlin';
    const example2 = 'Frau,Anna,Schmidt,anna.schmidt@example.com,,,,,,,Deutsch,Französisch,,,,,';
    const blob = new Blob([`${header}\n${example1}\n${example2}\n`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'dolmetscher_vorlage.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const sendInvite = async (id) => {
    if (!window.confirm('Einladungs-E-Mail mit Passwort-Setup-Link an diesen Dolmetscher senden?')) return;
    try {
      const res = await api.post(`/interpreters/${id}/invite`);
      toast.success(`Einladung gesendet an ${res.data.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Einladung fehlgeschlagen');
    }
  };

  const filtered = items.filter(c =>
    (!search ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.language_pairs || []).some(p =>
        p.source.toLowerCase().includes(search.toLowerCase()) ||
        p.target.toLowerCase().includes(search.toLowerCase())
      ) ||
      c.languages.some(l => l.toLowerCase().includes(search.toLowerCase())) ||
      c.target_languages.some(l => l.toLowerCase().includes(search.toLowerCase()))
    ) && (!swornOnly || c.sworn)
  );

  return (
    <AppLayout>
      <PageHeader title="Dolmetscher" description="Dolmetscherdatenbank verwalten und per CSV importieren">
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="interpreter-csv-template-btn" className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-md px-3 py-2 text-sm">
            <Download className="w-4 h-4" /> CSV-Vorlage
          </button>
          <button type="button" className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-md px-4 py-2 text-sm cursor-pointer" data-testid="csv-import-label" onClick={() => setCsvOpen(true)}>
            <Upload className="w-4 h-4" /> CSV-Import
          </button>
          <button onClick={openNew} data-testid="new-interpreter-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Neuer Dolmetscher
          </button>
        </div>
      </PageHeader>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, E-Mail, Sprache..."
            data-testid="interpreter-search"
            className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setSwornOnly(v => !v)}
          data-testid="sworn-filter-toggle"
          className={`inline-flex items-center gap-2 border rounded-md px-3 py-2 text-sm font-medium transition ${swornOnly ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
        >
          <ShieldCheck className="w-4 h-4" /> Nur vereidigte
        </button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={UserCog} title="Keine Dolmetscher gefunden" description="Legen Sie Dolmetscher an oder importieren Sie per CSV." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Name</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Sprachpaare</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Fachgebiete</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Honorar</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`interpreter-row-${c.id}`}>
                  <td className="border-b border-slate-100 py-3 px-4">
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      <span>{c.salutation ? `${c.salutation} ` : ''}{c.first_name} {c.last_name}</span>
                      {c.sworn && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#0F4C81] text-white rounded px-1.5 py-0.5" data-testid={`list-sworn-${c.id}`} title="Allgemein beeidigt / vereidigt">
                          <ShieldCheck className="w-3 h-3" /> Vereidigt
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-xs text-slate-700">
                    {(c.language_pairs && c.language_pairs.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {c.language_pairs.map((p, i) => (
                          <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded px-1.5 py-0.5 font-medium text-slate-700">
                            {p.source} ↔ {p.target}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-xs text-slate-700">{c.specializations.join(', ') || '-'}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700 font-mono">{c.custom_rate.toFixed(2)} €</td>
                  <td className="border-b border-slate-100 py-3 px-4">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded text-xs font-medium ${c.active_status ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                        {c.active_status ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      <span className="text-xs text-slate-500">{AVAILABILITY_STATUSES.find(a => a.value === c.availability_status)?.label}</span>
                      {c.user_id ? (
                        <span className="inline-flex w-fit items-center gap-1 text-[11px] text-slate-600" title={c.invited_at ? `Eingeladen am ${new Date(c.invited_at).toLocaleDateString('de-DE')}` : 'Hat ein Portal-Konto'}>
                          <CheckCircle className="w-3 h-3 text-green-600" /> Portal-Zugang
                        </span>
                      ) : (
                        <span className="inline-flex w-fit items-center gap-1 text-[11px] text-amber-700" title="Noch kein Portal-Konto. Bitte einladen.">
                          <Mail className="w-3 h-3" /> Kein Zugang
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-right">
                    {!c.user_id ? (
                      <button
                        onClick={() => sendInvite(c.id)}
                        data-testid={`invite-interpreter-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded px-2 py-1 mr-2"
                        title="Einladungs-E-Mail mit Passwort-Setup-Link senden"
                      >
                        <Send className="w-3 h-3" /> Einladen
                      </button>
                    ) : c.invited_at ? (
                      <button
                        onClick={() => sendInvite(c.id)}
                        data-testid={`reinvite-interpreter-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#0F4C81] hover:bg-slate-100 rounded px-2 py-1 mr-2"
                        title="Einladung erneut senden (alter Link wird ungültig)"
                      >
                        <Send className="w-3 h-3" /> Erneut
                      </button>
                    ) : null}
                    <button onClick={() => openEdit(c)} data-testid={`edit-interpreter-${c.id}`} className="text-slate-600 hover:text-[#0F4C81] mr-3"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(c.id)} data-testid={`delete-interpreter-${c.id}`} className="text-slate-600 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            CSV-Tipp: Pro Zeile ein Dolmetscher + ein Sprachpaar (source_language / target_language). Tauchen Name + E-Mail mehrfach auf, werden die Sprachpaare automatisch kumuliert.
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md max-w-2xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">{editing ? 'Dolmetscher bearbeiten' : 'Neuer Dolmetscher'}</h3>
            <form onSubmit={save} className="space-y-3" data-testid="interpreter-form">
              <div className="grid grid-cols-[140px_1fr_1fr] gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Anrede</label>
                  <select value={form.salutation || ''} onChange={(e) => setForm(f => ({ ...f, salutation: e.target.value }))} data-testid="int-field-salutation" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                    <option value="">—</option>
                    <option value="Herr">Herr</option>
                    <option value="Frau">Frau</option>
                    <option value="Divers">Divers</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Vorname *</label>
                  <input required value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="int-field-first_name" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nachname *</label>
                  <input required value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} data-testid="int-field-last_name" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">E-Mail *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="int-field-email" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Telefon</label>
                  <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Adresse (für Honorar-Abrechnung)</label>
                <div className="grid grid-cols-[1fr_90px] gap-2 mb-2">
                  <input value={form.street || ''} onChange={(e) => setForm(f => ({ ...f, street: e.target.value }))} placeholder="Straße" data-testid="int-field-street" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={form.house_number || ''} onChange={(e) => setForm(f => ({ ...f, house_number: e.target.value }))} placeholder="Hausnr." data-testid="int-field-housenr" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <input value={form.postal_code || ''} onChange={(e) => setForm(f => ({ ...f, postal_code: e.target.value }))} placeholder="PLZ" data-testid="int-field-plz" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={form.city || ''} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Ort" data-testid="int-field-city" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Sprachpaare (bidirektional ↔)</label>
                <LanguagePairsInput
                  value={form.language_pairs || []}
                  onChange={(pairs) => setForm(f => ({ ...f, language_pairs: pairs }))}
                  testidPrefix="int-pairs"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Fachgebiete</label>
                  <MultiSelect options={SPECIALIZATIONS} selected={form.specializations} onChange={(v) => setForm(f => ({ ...f, specializations: v }))} placeholder="Wählen..." testid="int-specializations" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Einsatzarten</label>
                  <MultiSelect options={SERVICE_TYPES} selected={form.service_types} onChange={(v) => setForm(f => ({ ...f, service_types: v }))} placeholder="Wählen..." testid="int-service-types" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Honorar (€/Std)</label>
                  <input type="number" step="0.01" value={form.custom_rate} onChange={(e) => setForm(f => ({ ...f, custom_rate: parseFloat(e.target.value) || 0 }))} data-testid="int-field-rate" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Verfügbarkeit</label>
                  <select value={form.availability_status} onChange={(e) => setForm(f => ({ ...f, availability_status: e.target.value }))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                    {AVAILABILITY_STATUSES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.active_status} onChange={(e) => setForm(f => ({ ...f, active_status: e.target.checked }))} data-testid="int-field-active" className="w-4 h-4" />
                    Aktiv
                  </label>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
                <label className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                  <input type="checkbox" checked={!!form.sworn} onChange={(e) => setForm(f => ({ ...f, sworn: e.target.checked }))} data-testid="int-field-sworn" className="w-4 h-4" />
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-[#0F4C81]" />
                    <span className="font-medium">Allgemein beeidigt / vereidigt</span>
                  </span>
                </label>
                <div className="text-xs text-slate-500 mt-1 ml-6">Darf gerichtsverwertbar dolmetschen (relevant für Kanzleien, Behörden, Gerichtstermine).</div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Notizen</label>
                <textarea rows="2" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
                <button type="submit" disabled={loading} data-testid="int-save-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50">
                  {loading ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CsvImportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onComplete={handleCsvComplete}
        title="Dolmetscher importieren"
        previewEndpoint="/interpreters/import-csv/preview"
        commitEndpoint="/interpreters/import-csv/commit"
        testidPrefix="interpreter-csv"
      />
    </AppLayout>
  );
};

export default AdminInterpreters;
